import type { SVGProps } from 'react';

/**
 * Inline single-color icons used across the BFM Online (Barita) portal chrome.
 * They inherit `currentColor`, so colour them with text utilities. Sourced to
 * visually match the Figma vector glyphs (footer utility row + social links).
 */
type IconProps = SVGProps<SVGSVGElement>;

export function PhoneIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function NewsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        d="M18 3H6a2 2 0 0 0-2 2v13a3 3 0 0 0 3 3h11a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z"
        strokeLinejoin="round"
      />
      <path d="M7 21a3 3 0 0 1-3-3" strokeLinecap="round" />
      <path d="M8 7h8M8 11h8M8 15h5" strokeLinecap="round" />
    </svg>
  );
}

export function VideoIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <rect x="2" y="6" width="14" height="12" rx="2" strokeLinejoin="round" />
      <path d="m16 10 6-3v10l-6-3" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} {...props}>
      <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" strokeLinejoin="round" />
      <path d="M3 9h18M8 3v3M16 3v3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" strokeLinejoin="round" />
      <path d="m3 6 9 7 9-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} {...props}>
      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function UserAvatarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} {...props}>
      <circle cx="12" cy="12" r="11" fill="currentColor" opacity={0.12} stroke="none" />
      <circle cx="12" cy="9.5" r="3.4" />
      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Personal-information glyph — filled person inside a ring (Figma My Profile
 * 68:182). Exact vector; inherits `currentColor`.
 */
export function PersonalInfoIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 36 36" fill="currentColor" {...props}>
      <path d="M18 0C8.0747 0 0 8.0747 0 18C0 27.9253 8.0747 36 18 36C27.9253 36 36 27.9253 36 18C36 8.0747 27.9253 0 18 0ZM28.9265 28.843C27.5463 26.0047 24.6366 24.0458 21.2697 24.0458H14.7303C11.3634 24.0458 8.45365 26.0047 7.07345 28.843C4.30902 26.0575 2.59794 22.2252 2.59794 18C2.59794 9.50723 9.50741 2.59794 18 2.59794C26.4926 2.59794 33.4021 9.50723 33.4021 18C33.4021 22.2252 31.691 26.0575 28.9265 28.843Z" />
      <path d="M17.9988 21.4606C21.821 21.4606 24.9196 18.3621 24.9196 14.5399C24.9196 10.7176 21.821 7.61914 17.9988 7.61914C14.1766 7.61914 11.0781 10.7176 11.0781 14.5399C11.0781 18.3621 14.1766 21.4606 17.9988 21.4606Z" />
    </svg>
  );
}

/**
 * Contact-information glyph — envelope with a phone handset (Figma My Profile
 * 394:2942). Exact vector; inherits `currentColor`.
 */
export function ContactInfoIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 39 36" fill="currentColor" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.0067 5.8707H37.1571L23.4069 15.2969L14.0539 8.88509C14.41 7.93158 14.4131 6.85378 14.0067 5.8707ZM7.95211 11.3417L11.3265 9.28712C12.3279 8.6774 12.6603 7.37651 12.074 6.3611L9.02763 1.08458C8.71512 0.543194 8.24207 0.194923 7.63235 0.0572756C7.02255 -0.0804529 6.4459 0.0307236 5.93098 0.385266L0.375257 4.21039C-2.26928 13.5284 9.61471 33.7014 18.7291 36L24.8196 33.1012C25.384 32.8326 25.7687 32.3887 25.9543 31.7917C26.1399 31.1948 26.0748 30.6111 25.7622 30.0697L22.7159 24.7932C22.1297 23.7778 20.8368 23.4152 19.8081 23.9776L16.3416 25.8725C12.9134 22.8237 8.8785 15.835 7.95211 11.3417ZM38.8385 7.08297L23.9567 17.285C23.6085 17.5226 23.1611 17.5041 22.8375 17.2715L12.9579 10.4987C12.7707 10.6674 12.5657 10.8212 12.3431 10.9568L10.2088 12.2562C11.3301 15.875 14.1046 20.6805 16.6779 23.4609L18.8704 22.2623C20.8294 21.1915 23.2923 21.8823 24.4086 23.8158L26.2979 27.088H37.3021C38.171 27.088 38.8819 26.3772 38.8819 25.5083V7.45047C38.882 7.32406 38.8668 7.20099 38.8385 7.08297Z"
      />
    </svg>
  );
}

/** Nav account avatar — person inside a ring (Figma dashboard 43:426). */
export function ProfileCircleIcon(props: IconProps) {
  return (
    <svg viewBox="1809 42 36 36" fill="currentColor" {...props}>
      <path d="M1827.01 61.2008C1825.59 61.2008 1824.2 60.7785 1823.01 59.9874C1821.83 59.1962 1820.91 58.0717 1820.36 56.7561C1819.82 55.4405 1819.67 53.9928 1819.95 52.5961C1820.23 51.1995 1820.91 49.9166 1821.92 48.9096C1822.93 47.9027 1824.21 47.2169 1825.61 46.9391C1827 46.6613 1828.45 46.8039 1829.77 47.3489C1831.08 47.8938 1832.21 48.8166 1833 50.0007C1833.79 51.1847 1834.21 52.5768 1834.21 54.0008C1834.21 55.9103 1833.45 57.7417 1832.1 59.0919C1830.75 60.4422 1828.92 61.2008 1827.01 61.2008ZM1827.01 49.2008C1826.06 49.2008 1825.13 49.4823 1824.35 50.0097C1823.56 50.5372 1822.94 51.2868 1822.58 52.1639C1822.21 53.041 1822.12 54.0061 1822.3 54.9372C1822.49 55.8683 1822.95 56.7236 1823.62 57.3949C1824.29 58.0662 1825.14 58.5233 1826.08 58.7085C1827.01 58.8938 1827.97 58.7987 1828.85 58.4354C1829.73 58.0721 1830.48 57.4569 1831 56.6675C1831.53 55.8782 1831.81 54.9501 1831.81 54.0008C1831.81 52.7277 1831.31 51.5068 1830.41 50.6067C1829.51 49.7065 1828.29 49.2008 1827.01 49.2008Z" />
      <path d="M1827.01 77.9998C1824.35 77.9981 1821.72 77.4063 1819.32 76.2671C1816.91 75.1279 1814.79 73.4697 1813.1 71.4118L1812.48 70.6438L1813.1 69.8878C1814.79 67.8326 1816.92 66.1772 1819.32 65.0406C1821.73 63.904 1824.35 63.3145 1827.01 63.3145C1829.67 63.3145 1832.3 63.904 1834.7 65.0406C1837.11 66.1772 1839.23 67.8326 1840.92 69.8878L1841.54 70.6438L1840.92 71.4118C1839.23 73.4697 1837.11 75.1279 1834.71 76.2671C1832.3 77.4063 1829.67 77.9981 1827.01 77.9998ZM1815.62 70.6558C1817.08 72.2182 1818.85 73.464 1820.81 74.3157C1822.77 75.1674 1824.89 75.6069 1827.02 75.6069C1829.16 75.6069 1831.28 75.1674 1833.24 74.3157C1835.2 73.464 1836.96 72.2182 1838.42 70.6558C1836.96 69.0934 1835.2 67.8477 1833.24 66.996C1831.28 66.1443 1829.16 65.7048 1827.02 65.7048C1824.89 65.7048 1822.77 66.1443 1820.81 66.996C1818.85 67.8477 1817.08 69.0934 1815.62 70.6558Z" />
      <path d="M1827.01 78C1822.92 78.0027 1818.96 76.6133 1815.76 74.0605C1812.57 71.5077 1810.34 67.9436 1809.44 63.9548C1808.54 59.966 1809.03 55.7902 1810.82 52.1147C1812.61 48.4392 1815.6 45.4831 1819.29 43.7327C1822.99 41.9824 1827.17 41.5423 1831.15 42.4847C1835.13 43.4272 1838.67 45.6961 1841.18 48.9181C1843.7 52.14 1845.05 56.1229 1845 60.2114C1844.95 64.2998 1843.51 68.2501 1840.92 71.412C1839.23 73.4699 1837.11 75.1281 1834.71 76.2673C1832.3 77.4064 1829.67 77.9982 1827.01 78ZM1827.01 44.4C1823.93 44.4 1820.91 45.3149 1818.35 47.0291C1815.78 48.7432 1813.78 51.1796 1812.6 54.0301C1811.42 56.8807 1811.11 60.0173 1811.71 63.0434C1812.31 66.0695 1813.8 68.8492 1815.98 71.0308C1818.16 73.2125 1820.94 74.6983 1823.97 75.3002C1826.99 75.9022 1830.13 75.5932 1832.98 74.4125C1835.83 73.2318 1838.27 71.2323 1839.98 68.6669C1841.7 66.1015 1842.61 63.0854 1842.61 60C1842.61 55.8626 1840.97 51.8947 1838.04 48.9691C1835.12 46.0436 1831.15 44.4 1827.01 44.4Z" />
      <path d="M1814.03 70.6558C1814.03 70.6558 1826.11 84.1558 1838.41 71.9998L1840 70.6558C1840 70.6558 1829.72 59.9998 1819.3 66.3958L1814.03 70.6558Z" />
      <path d="M1827.01 60C1830.33 60 1833.01 57.3137 1833.01 54C1833.01 50.6863 1830.33 48 1827.01 48C1823.7 48 1821.01 50.6863 1821.01 54C1821.01 57.3137 1823.7 60 1827.01 60Z" />
    </svg>
  );
}

/** Relationship Manager — suited advisor silhouette (Figma dashboard 43:426). */
export function RelationshipManagerIcon(props: IconProps) {
  return (
    <svg viewBox="1809 176 36 36" fill="currentColor" {...props}>
      <path d="M1827 195.637C1832.42 195.637 1836.82 191.241 1836.82 185.818C1836.82 180.396 1832.42 176 1827 176C1821.58 176 1817.18 180.396 1817.18 185.818C1817.18 191.241 1821.58 195.637 1827 195.637Z" />
      <path d="M1840.97 200.961C1839.43 200.168 1836.96 199.055 1834.02 198.26C1833.61 198.145 1833.17 198.373 1833.03 198.774L1829.68 208.13L1828.66 203.052L1830.23 198.349C1830.31 198.099 1830.27 197.824 1830.12 197.611C1829.96 197.398 1829.72 197.271 1829.45 197.271H1824.55C1824.28 197.271 1824.04 197.398 1823.88 197.611C1823.73 197.824 1823.69 198.099 1823.77 198.349L1825.34 203.052L1824.32 208.13L1820.97 198.774C1820.83 198.373 1820.39 198.146 1819.98 198.26C1817.04 199.055 1814.57 200.168 1813.03 200.961C1810.54 202.237 1809 204.754 1809 207.529V208.726C1809 210.531 1810.47 211.999 1812.27 211.999H1824.55H1829.45H1841.73C1843.53 211.999 1845 210.531 1845 208.726V207.529C1845 204.754 1843.46 202.237 1840.97 200.961Z" />
    </svg>
  );
}

export function ArrowUpRightIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M7 17 17 7M8 7h9v9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ArrowDownLeftIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M17 7 7 17M16 17H7V8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TransferIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M4 8h13l-3-3M20 16H7l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Transfer IN — diagonal arrow to the bottom-right (Figma dashboard 43:426). */
export function TransferInIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeMiterlimit={2}
      {...props}
    >
      <path d="M4 4 12 12" />
      <path d="M12 4V12H4" />
    </svg>
  );
}

/** Transfer Out — diagonal arrow to the top-right (Figma dashboard 43:426). */
export function TransferOutIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeMiterlimit={2}
      {...props}
    >
      <path d="M4 12 12 4" />
      <path d="M12 12V4H4" />
    </svg>
  );
}

/** Bond Int Payment — award/certificate badge (Figma dashboard 43:426). */
export function BondPaymentIcon(props: IconProps) {
  return (
    <svg viewBox="428.5 760.5 14 14" fill="currentColor" {...props}>
      <path d="M440.167 761H430.833C429.82 761 429 761.82 429 762.833V768.833C429 769.847 429.82 770.667 430.833 770.667H432.82C432.213 770.013 431.833 769.133 431.833 768.167C431.833 766.147 433.48 764.5 435.5 764.5C437.52 764.5 439.167 766.147 439.167 768.167C439.167 769.133 438.787 770.013 438.18 770.667H440.167C441.18 770.667 442 769.847 442 768.833V762.833C442 761.82 441.18 761 440.167 761ZM438.833 763.333H432.167C431.891 763.333 431.667 763.109 431.667 762.833C431.667 762.557 431.891 762.333 432.167 762.333H438.833C439.109 762.333 439.333 762.557 439.333 762.833C439.333 763.109 439.109 763.333 438.833 763.333ZM438.167 768.167C438.167 769.639 436.973 770.833 435.5 770.833C434.027 770.833 432.833 769.639 432.833 768.167C432.833 766.694 434.027 765.5 435.5 765.5C436.973 765.5 438.167 766.694 438.167 768.167ZM435.5 771.833C436.24 771.833 436.927 771.613 437.5 771.233V773.242C437.5 773.697 437.054 774.019 436.623 773.875L435.711 773.571C435.574 773.525 435.426 773.525 435.289 773.571L434.377 773.875C433.945 774.019 433.5 773.697 433.5 773.242V771.233C434.073 771.613 434.76 771.833 435.5 771.833Z" />
    </svg>
  );
}

/** Withdrawal — ATM cash dispenser with a down arrow (Figma dashboard 43:426). */
export function WithdrawalIcon(props: IconProps) {
  return (
    <svg viewBox="426.5 883.5 16 15" fill="currentColor" {...props}>
      <path d="M438.686 887.314H439.209C439.209 887.175 439.154 887.042 439.056 886.944C438.958 886.846 438.825 886.791 438.686 886.791V887.314ZM430.314 887.314V886.791C430.175 886.791 430.042 886.846 429.944 886.944C429.846 887.042 429.791 887.175 429.791 887.314H430.314ZM438.163 887.314V890.105H439.209V887.314H438.163ZM438.163 890.105V895.686H439.209V890.105H438.163ZM437.291 896.558H431.709V897.605H437.291V896.558ZM430.837 895.686V887.314H429.791V895.686H430.837ZM430.314 887.837H438.686V886.791H430.314V887.837ZM430.314 885.047H438.686V884H430.314V885.047ZM428.047 887.314C428.047 886.062 429.062 885.047 430.314 885.047V884C428.484 884 427 885.484 427 887.314H428.047ZM430.314 889.581C429.062 889.581 428.047 888.566 428.047 887.314H427C427 889.144 428.484 890.628 430.314 890.628V889.581ZM440.953 887.314C440.953 888.566 439.938 889.581 438.686 889.581V890.628C440.516 890.628 442 889.144 442 887.314H440.953ZM442 887.314C442 885.484 440.516 884 438.686 884V885.047C439.938 885.047 440.953 886.062 440.953 887.314H442ZM438.163 895.686C438.163 896.168 437.772 896.558 437.291 896.558V897.605C438.35 897.605 439.209 896.746 439.209 895.686H438.163ZM431.709 896.558C431.228 896.558 430.837 896.168 430.837 895.686H429.791C429.791 896.746 430.65 897.605 431.709 897.605V896.558Z" />
      <path d="M436.265 893.264C436.469 893.06 436.469 892.728 436.265 892.524C436.061 892.32 435.729 892.32 435.525 892.524L436.265 893.264ZM433.474 892.524C433.27 892.32 432.939 892.32 432.734 892.524C432.53 892.728 432.53 893.06 432.734 893.264L433.474 892.524ZM435.023 890.103C435.023 889.814 434.789 889.58 434.5 889.58C434.211 889.58 433.976 889.814 433.976 890.103H435.023ZM433.976 893.592C433.976 893.881 434.211 894.115 434.5 894.115C434.789 894.115 435.023 893.881 435.023 893.592H433.976ZM435.525 892.524L434.623 893.426L435.363 894.166L436.265 893.264L435.525 892.524ZM434.376 893.426L433.474 892.524L432.734 893.264L433.636 894.166L434.376 893.426ZM434.623 893.426C434.555 893.494 434.444 893.494 434.376 893.426L433.636 894.166C434.113 894.643 434.886 894.643 435.363 894.166L434.623 893.426ZM433.976 890.103V893.592H435.023V890.103H433.976Z" />
    </svg>
  );
}

export function FileExportIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" strokeLinejoin="round" />
      <path d="M12 18v-6m0 0-2.5 2.5M12 12l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Contract-note indicator — the filled "PDF" document badge shown to the LEFT of
 * the date in the Transactions Operation Date cell (Figma 165:631, node fi_157933:
 * a solid charcoal page with a folded top-right corner and white "PDF" label).
 * Colour it via `currentColor`.
 */
export function ContractNoteIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 22 26" fill="none" {...props}>
      {/* Page body with a folded top-right corner. */}
      <path
        d="M3 1h10l7 7v15a2.5 2.5 0 0 1-2.5 2.5h-12A2.5 2.5 0 0 1 3 23V1Z"
        transform="translate(-1 0)"
        fill="currentColor"
      />
      <path
        d="M12 1v6a1 1 0 0 0 1 1h6"
        fill="none"
        stroke="#fff"
        strokeWidth={1.1}
        strokeOpacity={0.85}
        strokeLinejoin="round"
      />
      <text
        x="10.5"
        y="19.5"
        textAnchor="middle"
        fill="#fff"
        fontSize="6.2"
        fontWeight="700"
        fontFamily="inherit"
        letterSpacing="0.2"
      >
        PDF
      </text>
    </svg>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.4 5.2A9.4 9.4 0 0 1 12 5c7 0 10.5 7 10.5 7a16.4 16.4 0 0 1-3.4 4.3M6.2 6.2A16.3 16.3 0 0 0 1.5 12S5 19 12 19a9.6 9.6 0 0 0 3.1-.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TwitterIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M23 4.9c-.8.36-1.67.6-2.58.71a4.5 4.5 0 0 0 1.98-2.48 9 9 0 0 1-2.85 1.09 4.49 4.49 0 0 0-7.65 4.1A12.74 12.74 0 0 1 2.6 3.6a4.49 4.49 0 0 0 1.39 5.99c-.73-.02-1.41-.22-2.01-.55v.05a4.49 4.49 0 0 0 3.6 4.4c-.66.18-1.36.21-2.03.08a4.5 4.5 0 0 0 4.19 3.12A9.02 9.02 0 0 1 1 18.57 12.72 12.72 0 0 0 7.89 20.6c8.27 0 12.79-6.85 12.79-12.79l-.01-.58A9.1 9.1 0 0 0 23 4.9Z" />
    </svg>
  );
}

export function InstagramIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function LinkedInIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm6 0h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05C20.6 8.59 22 10.3 22 13.6V21h-4v-6.56c0-1.56-.03-3.57-2.18-3.57-2.18 0-2.51 1.7-2.51 3.46V21H9V9Z" />
    </svg>
  );
}

export function FacebookIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M14 8.5h2.5V5H14c-2.2 0-3.5 1.5-3.5 3.7V11H8v3.5h2.5V22H14v-7.5h2.5L17 11h-3V9c0-.4.3-.5.6-.5H14Z" />
    </svg>
  );
}

export function YouTubeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M23 12s0-3.3-.42-4.88a2.53 2.53 0 0 0-1.78-1.79C19.22 5 12 5 12 5s-7.22 0-8.8.34A2.53 2.53 0 0 0 1.42 7.12C1 8.7 1 12 1 12s0 3.3.42 4.88c.23.86.91 1.54 1.78 1.78C4.78 19 12 19 12 19s7.22 0 8.8-.34a2.53 2.53 0 0 0 1.78-1.78C23 15.3 23 12 23 12ZM9.75 15.02V8.98L15.5 12l-5.75 3.02Z" />
    </svg>
  );
}
